import { useState } from "react";
import {
	CardActionArea,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	IconButton,
	SvgIcon,
	Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
// SVG Icons
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";
import PhoneNumbersFormArea from "./PhoneNumbersFormArea";
import { TCountrySelected } from "@/Settings/types/SystemSettings/callActivity";
import SearchInput from "@/Layouts/components/Basics/SearchInput";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import { IPhoneObject } from "@/Settings/types/Common/model";
import { formatPhoneNumber } from "@/Common/helpers/Modifiers/formatters";
import ActionCard from "@Layouts/components/Cards/ActionCard/ActionCard";

const getFilteredPhoneNumbers = (phoneNumbers?: IPhoneObject[], search = "") => {
	if (search === "") {
		return phoneNumbers ?? [];
	}
	return (
		phoneNumbers?.filter((phoneNumber) => {
			return phoneNumber.phoneNumber.replace(/\D/g, "").includes(search.replace(/\D/g, ""));
		}) ?? []
	);
};

type TCallActivityBlackListDialogProps = {
	locationId: string;
	openDialog: boolean;
	disabled: boolean;
	numbers: string;
	countrySelected?: TCountrySelected | null;
	error?: boolean;
	phoneNumbers?: IPhoneObject[];
	isDeleting: boolean;
	toDeletePhoneNumbers: string[];
	onChangeCountry: (value: TCountrySelected) => void;
	clearNumbers: (value: string) => void;
	handleInputChange: (value: string, mask?: string) => void;
	onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	onRemovePhoneNumber: (phoneNumbers: IPhoneObject[]) => void;
	onAddButtonClick: () => void;
	onCloseDialog: () => void;
};

const CallActivityBlackListDialog = (props: TCallActivityBlackListDialogProps) => {
	const {
		phoneNumbers = [],
		openDialog,
		onCloseDialog,
		locationId,
		disabled,
		numbers,
		countrySelected,
		onChangeCountry,
		clearNumbers,
		handleInputChange,
		onKeyDown,
		onAddButtonClick,
		onRemovePhoneNumber,
		toDeletePhoneNumbers,
		isDeleting,
		error,
	} = props;

	const [searchValue, setSearchValue] = useState("");
	const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<IPhoneObject[]>([]);

	const filteredPhoneNumbers = getFilteredPhoneNumbers(phoneNumbers, searchValue);
	const selectAllChecked = selectedPhoneNumbers.length !== 0 && selectedPhoneNumbers.length === filteredPhoneNumbers.length;

	const handleDialogClose = () => {
		setSelectedPhoneNumbers([]);
		setSearchValue("");
		onCloseDialog();
	};

	const handleInputSearch = (value: string) => {
		const formattedValue = formatPhoneNumber(value);
		setSearchValue(formattedValue);
	};

	const handleDeletePhoneNumbers = () => {
		onRemovePhoneNumber(selectedPhoneNumbers);
		setSearchValue("");
		setSelectedPhoneNumbers([]);
	};

	const handleChangeAllCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		if (checked) {
			setSelectedPhoneNumbers(getFilteredPhoneNumbers(phoneNumbers, searchValue));
		} else {
			setSelectedPhoneNumbers([]);
		}
	};

	const handleSelectedPhoneNumbers = (phoneNumber: IPhoneObject, action: string) => {
		setSelectedPhoneNumbers((prevPhoneNumbers: IPhoneObject[]) => {
			switch (action) {
				case "add":
					return [...prevPhoneNumbers, phoneNumber];
				case "remove":
					return prevPhoneNumbers.filter((prevPhoneNumber) => prevPhoneNumber !== phoneNumber);
				default:
					return prevPhoneNumbers;
			}
		});
	};

	return (
		<Dialog
			className={"dialog-call-activity"}
			open={openDialog}
			fullWidth={true}
			maxWidth={"md"}
			disableEscapeKeyDown
			onClose={handleDialogClose}
		>
			<DialogTitle>
				<IconButton
					aria-label="close"
					onClick={handleDialogClose}
					sx={{
						position: "absolute",
						right: 20,
						top: 10,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ overflow: "hidden", maxHeight: "150px" }}>
				<Grid container direction={"column"} spacing={4} paddingBottom={10}>
					<Grid item>
						<PhoneNumbersFormArea
							locationId={locationId}
							disabled={disabled}
							numbers={numbers}
							countrySelected={countrySelected}
							selectorPopperProps={{ className: "!z-[1350]" }}
							onChangeCountry={onChangeCountry}
							clearNumbers={clearNumbers}
							handleInputChange={handleInputChange}
							onKeyDown={onKeyDown}
							onAddButtonClick={onAddButtonClick}
							error={error}
						/>
					</Grid>
					<Grid item sx={{ width: "50%" }} alignSelf={"center"}>
						<SearchInput
							value={searchValue}
							loading={false}
							sx={{ backgroundColor: "transparent !important" }}
							onChange={handleInputSearch}
							clearSearch={() => {
								setSearchValue("");
							}}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogContent>
				<Grid item marginLeft={"11px"} marginBottom={2}>
					<Grid container justifyContent={"space-between"}>
						<FormControlLabel
							label={<Typography className={"regular-montserrat-label fs-18"}>Black Listed Phone Numbers</Typography>}
							control={<Checkbox color="primary" checked={selectAllChecked} onChange={handleChangeAllCheckbox} />}
						/>
						<DoubleCheckingButton
							title="Remove selected numbers"
							icon={
								<SvgIcon
									viewBox="0 0 20 20"
									className={
										selectedPhoneNumbers?.length === 0
											? "!text-[20px] ml-[3px]  text-gray-400"
											: "!text-[20px] ml-[3px]"
									}
									component={TrashIcon}
								/>
							}
							iconHover={<SvgIcon viewBox="0 0 20 20" className="!text-[20px] ml-[3px]" component={TrashIcon} />}
							endIcon={<SvgIcon viewBox="0 0 20 20" className="!text-[20px] ml-[3px]" component={TrashIcon} />}
							mainButtonClasses={"btn-simple-remove"}
							confirmationSx={{ width: "200px !important" }}
							onDeleteAction={() => handleDeletePhoneNumbers()}
							disabled={selectedPhoneNumbers?.length === 0}
						/>
					</Grid>
				</Grid>
				<Grid item marginTop={"-20px"}>
					<Grid container spacing={2}>
						{filteredPhoneNumbers?.map((phoneObject) => {
							return (
								<Grid key={phoneObject._id} item xs={2.4}>
									<PhoneNumberWithCheckbox
										isLoading={disabled}
										deleting={toDeletePhoneNumbers.includes(phoneObject.phoneNumber) && isDeleting}
										onCheckPhoneNumber={handleSelectedPhoneNumbers}
										phoneObject={phoneObject}
										selectedPhoneNumbers={selectedPhoneNumbers}
									/>
								</Grid>
							);
						})}
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

export default CallActivityBlackListDialog;

type TPhoneNumberWithCheckboxProps = {
	phoneObject: IPhoneObject;
	isLoading: boolean;
	deleting: boolean;
	selectedPhoneNumbers: IPhoneObject[];
	onCheckPhoneNumber: (phoneObject: IPhoneObject, action: "add" | "remove") => void;
};

const PhoneNumberWithCheckbox = (props: TPhoneNumberWithCheckboxProps) => {
	const { phoneObject, isLoading, selectedPhoneNumbers, deleting } = props;
	const checked = selectedPhoneNumbers.includes(phoneObject);

	const handleCheckboxClick = () => {
		props.onCheckPhoneNumber(phoneObject, !checked ? "add" : "remove");
	};

	return (
		<CardActionArea disabled={isLoading} className="card-action-area-br-10" onClick={handleCheckboxClick}>
			<ActionCard
				title={
					<Typography
						className={"regular-pt-sans-pro-narrow-label bold !text-[11pt]"}
						component={"div"}
						noWrap
						sx={{ maxWidth: "290px" }}
					>
						{phoneObject.phoneNumber}
					</Typography>
				}
				classes={"action-card-br-10"}
				checkbox
				checked={checked}
				disabled={isLoading}
				loading={deleting}
				skeletonTitleWidth={95}
			/>
		</CardActionArea>
	);
};
